import React, { useState, useEffect } from 'react';
import { Edit2, Save, X } from 'lucide-react';
import { FormSection, Input, Button, SimCardBadge } from '../../common/ui';
import { formatDate, formatFullDateTime } from '../../../utils/dateUtils';
import { getSimCardOwner, isIccidAttribute } from '../../../utils/simCardUtils';

const ProductAttributesSection = ({ 
  productAttributes = [],
  deviceAttributeValues = {},
  readOnly = false, 
  deviceStatus = 'active',
  onSave,
  deviceMeta = null
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValues, setEditedValues] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Initialize edited values from deviceAttributeValues
  useEffect(() => {
    if (!isEditing) {
      setEditedValues(deviceAttributeValues);
    }
  }, [deviceAttributeValues, isEditing]);

  const handleEdit = () => {
    setEditedValues(deviceAttributeValues);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedValues(deviceAttributeValues);
    setIsEditing(false);
    setValidationError('');
  };

  const handleSave = async () => {
    // Validate required fields if device is not in draft status
    if (deviceStatus !== 'draft') {
      const missingAttributes = [];
      const visibleAttributes = productAttributes.filter(attr => !attr.hidden);
      for (const attr of visibleAttributes) {
        if (attr.required) {
          const value = editedValues[attr.name];
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            missingAttributes.push(attr.name);
          }
        }
      }
      
      if (missingAttributes.length > 0) {
        setValidationError(`The following required fields must be filled: ${missingAttributes.join(', ')}`);
        return;
      }
    }
    
    setValidationError('');
    if (onSave) {
      setIsSaving(true);
      try {
        await onSave(editedValues);
        setIsEditing(false);
      } catch (error) {
        console.error('Error saving product attributes:', error);
        setValidationError('Failed to save product attributes');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleChange = (attributeName, value) => {
    setEditedValues(prev => ({
      ...prev,
      [attributeName]: value
    }));
  };

  const renderActions = () => {
    if (readOnly) return null;

    if (isEditing) {
      return (
        <div className="flex gap-2">
          <Button
            onClick={handleSave}
            variant="primary"
            size="xs"
            icon={Save}
            loading={isSaving}
          >
            Save
          </Button>
          <Button
            onClick={handleCancel}
            variant="ghost"
            size="xs"
            icon={X}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </div>
      );
    }

    return (
      <button
        onClick={handleEdit}
        className="text-blue-600 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colors"
        title="Edit product attributes"
      >
        <Edit2 className="h-4 w-4" />
      </button>
    );
  };

  // Get visible attributes only (hidden/legacy attributes are handled as fallbacks in DeviceModules)
  const visibleAttributes = (productAttributes || []).filter(attr => !attr.hidden);

  // If no product attributes defined, check if there are device attributes that should be shown
  const hasDeviceAttributes = deviceAttributeValues && Object.keys(deviceAttributeValues).length > 0;
  
  // Don't render if no product attributes and no device attributes
  if ((!productAttributes || productAttributes.length === 0) && !hasDeviceAttributes) {
    return null;
  }
  
  // If no product attributes but device has attributes, show a message
  if ((!productAttributes || productAttributes.length === 0) && hasDeviceAttributes) {
    return (
      <FormSection title="Device attributes">
        <div className="text-sm text-gray-600 mb-4">
          No product attributes are defined for this product. However, this device has the following attribute values stored:
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5">
          {Object.entries(deviceAttributeValues)
            .filter(([name, value]) => name && name.trim()) // Filter out empty names
            .map(([name, value]) => {
              // Remove trailing colon if present in the name
              const displayName = name.trim().replace(/:$/, '');
              const displayValue = value && value.toString().trim() ? value.toString().trim() : 'Not set';
              
              const simOwner = isIccidAttribute(displayName) ? getSimCardOwner(value) : null;

              return (
                <div key={name} className="flex flex-col sm:flex-row sm:items-start">
                  <span className="text-gray-700 font-medium sm:w-40 flex-shrink-0">{displayName}:</span>
                  <span className="sm:ml-2 text-gray-900 break-words">
                    {simOwner ? <SimCardBadge iccid={value} /> : displayValue}
                  </span>
                </div>
              );
            })}
        </div>
      </FormSection>
    );
  }

  // Helper function to check if an attribute value is non-empty
  const hasNonEmptyValue = (attr, values) => {
    const value = values[attr.name];
    
    // If value doesn't exist, it's empty
    if (value === null || value === undefined) {
      return false;
    }
    
    // For boolean attributes, false is a valid value (shows as "No")
    if (attr.type === 'boolean') {
      return value === 'true' || value === 'false' || value === true || value === false;
    }
    
    // For number attributes, 0 is a valid value
    if (attr.type === 'number') {
      return value !== null && value !== undefined && value !== '';
    }
    
    // For string attributes, check if it's not empty or whitespace-only
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    
    // For other types, check if value exists
    return value !== null && value !== undefined && value !== '';
  };

  const renderAttributeValue = (attr, values) => {
    const value = values[attr.name];
    if (attr.type === 'boolean') {
      return value === 'true' ? 'Yes' : 'No';
    }
    return value || 'Not set';
  };

  const renderAttributeDisplay = (attr, values, isReadOnly = false) => {
    const displayValue = renderAttributeValue(attr, values);
    // ICCIDs are shown as a two-line bubble naming the SIM owner, so the row still fits one grid column
    const simOwner = isIccidAttribute(attr.name) ? getSimCardOwner(values[attr.name]) : null;

    return (
      <div key={attr.name} className="flex flex-col sm:flex-row sm:items-start">
        <span className="text-gray-700 font-medium sm:w-40 flex-shrink-0">
          {attr.name}
          {attr.required && <span className="text-red-500 ml-1">*</span>}:
        </span>
        <span className="sm:ml-2 text-gray-900 break-words">
          {simOwner ? <SimCardBadge iccid={values[attr.name]} /> : displayValue}
        </span>
      </div>
    );
  };

  const renderAttributeInput = (attr, values) => {
    return (
      <div key={attr.name}>
        <label className="block text-xs font-medium text-gray-700 mb-0.5">
          {attr.name}
          {attr.required && <span className="text-red-500 ml-1">*</span>}
          {isIccidAttribute(attr.name) && (
            <SimCardBadge iccid={values[attr.name]} showIccid={false} className="ml-2 align-middle" />
          )}
        </label>
        {attr.type === 'boolean' ? (
          <select
            value={values[attr.name] || 'false'}
            onChange={(e) => handleChange(attr.name, e.target.value)}
            className="w-full h-7 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
          >
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        ) : attr.type === 'dropdown' ? (
          <select
            value={values[attr.name] || attr.default_value || ''}
            onChange={(e) => handleChange(attr.name, e.target.value)}
            className="w-full h-7 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
          >
            <option value="">Select...</option>
            {attr.options && Array.isArray(attr.options) && attr.options.map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        ) : attr.type === 'date' ? (
          <Input
            type="date"
            size="sm"
            value={values[attr.name] || ''}
            onChange={(e) => handleChange(attr.name, e.target.value)}
          />
        ) : attr.type === 'number' ? (
          <Input
            type="number"
            size="sm"
            value={values[attr.name] || ''}
            onChange={(e) => handleChange(attr.name, e.target.value)}
            placeholder={`Enter ${attr.name.toLowerCase()}`}
          />
        ) : (
          <Input
            type="text"
            size="sm"
            value={values[attr.name] || ''}
            onChange={(e) => handleChange(attr.name, e.target.value)}
            placeholder={`Enter ${attr.name.toLowerCase()}`}
          />
        )}
      </div>
    );
  };

  // Build meta fields for display
  const buildMetaFields = () => {
    if (!deviceMeta || typeof deviceMeta !== 'object') {
      return [];
    }

    const metaFields = [];
    
    // Distribution channel
    if (deviceMeta.distribution_channel) {
      metaFields.push({
        label: 'Channel',
        value: deviceMeta.distribution_channel
      });
    }
    
    // Location (as Google Maps link)
    if (deviceMeta.location) {
      let locationValue = null;
      let googleMapsUrl = null;
      const locationObj = deviceMeta.location;
      
      // Helper function to convert radians to degrees
      const radiansToDegrees = (radians) => {
        if (typeof radians !== 'number') return radians;
        return radians * (180 / Math.PI);
      };
      
      // Helper function to format coordinates
      const formatCoords = (lat, lon) => {
        const latDeg = radiansToDegrees(lat);
        const lonDeg = radiansToDegrees(lon);
        return `${latDeg.toFixed(6)},${lonDeg.toFixed(6)}`;
      };
      
      // Handle location as object (with latLng property)
      if (typeof locationObj === 'object' && locationObj !== null) {
        if (locationObj.latLng) {
          // Extract coordinates from latLng (could be string "lat,lon" or object with lat/lon)
          const coords = locationObj.latLng;
          if (typeof coords === 'string') {
            // If it's a string, assume it's already in degrees
            locationValue = coords;
            googleMapsUrl = `https://www.google.com/maps?q=${encodeURIComponent(coords)}`;
          } else if (typeof coords === 'object' && coords !== null) {
            // Handle object with lat/lon properties (in radians)
            if (coords.lat !== undefined && coords.lon !== undefined) {
              locationValue = formatCoords(coords.lat, coords.lon);
              const latDeg = radiansToDegrees(coords.lat);
              const lonDeg = radiansToDegrees(coords.lon);
              googleMapsUrl = `https://www.google.com/maps?q=${latDeg},${lonDeg}`;
            } else if (coords.latitude !== undefined && coords.longitude !== undefined) {
              locationValue = formatCoords(coords.latitude, coords.longitude);
              const latDeg = radiansToDegrees(coords.latitude);
              const lonDeg = radiansToDegrees(coords.longitude);
              googleMapsUrl = `https://www.google.com/maps?q=${latDeg},${lonDeg}`;
            }
          }
        } else {
          // If it's an object but no latLng, try to extract lat/lon directly (in radians)
          if (locationObj.lat !== undefined && locationObj.lon !== undefined) {
            locationValue = formatCoords(locationObj.lat, locationObj.lon);
            const latDeg = radiansToDegrees(locationObj.lat);
            const lonDeg = radiansToDegrees(locationObj.lon);
            googleMapsUrl = `https://www.google.com/maps?q=${latDeg},${lonDeg}`;
          } else if (locationObj.latitude !== undefined && locationObj.longitude !== undefined) {
            locationValue = formatCoords(locationObj.latitude, locationObj.longitude);
            const latDeg = radiansToDegrees(locationObj.latitude);
            const lonDeg = radiansToDegrees(locationObj.longitude);
            googleMapsUrl = `https://www.google.com/maps?q=${latDeg},${lonDeg}`;
          }
        }
      } else if (typeof locationObj === 'string') {
        // Check if it's coordinates (lat,lon format) or an address
        locationValue = locationObj;
        const isCoordinates = /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/.test(locationValue);
        googleMapsUrl = isCoordinates
          ? `https://www.google.com/maps?q=${encodeURIComponent(locationValue)}`
          : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationValue)}`;
      }
      
      // Only add if we have a valid location value
      if (locationValue) {
        metaFields.push({
          label: 'Location',
          value: (
            <>
              {googleMapsUrl ? (
                <a
                  href={googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  {locationValue}
                </a>
              ) : locationValue}
              {deviceMeta.last_seen && (
                <div className="text-gray-500 text-xs">
                  updated {formatFullDateTime(deviceMeta.last_seen)}
                </div>
              )}
            </>
          )
        });
      }
    }
    
    // Last seen
    if (deviceMeta.last_seen) {
      metaFields.push({
        label: 'Last Seen',
        value: formatDate(deviceMeta.last_seen)
      });
    }
    
    return metaFields;
  };

  const metaFields = buildMetaFields();
  const hasContent = visibleAttributes.length > 0 || metaFields.length > 0;

  return (
    <>
      {/* Main Section - Visible Attributes + Meta Fields */}
      {hasContent && (
        <FormSection title="Device attributes" actions={renderActions()}>
          {validationError && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{validationError}</p>
            </div>
          )}
          <div className="space-y-3">
            {isEditing ? (
              <>
                {/* Edit Mode */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {visibleAttributes.map((attr) => renderAttributeInput(attr, editedValues))}
                </div>
              </>
            ) : (
              <>
                {/* Read-Only Mode - Compact 2-column grid on desktop, single column on mobile */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5">
                  {visibleAttributes.map((attr) => renderAttributeDisplay(attr, deviceAttributeValues))}
                  {metaFields.map((field, idx) => (
                    <div key={`meta-${idx}`} className="flex flex-col sm:flex-row sm:items-start">
                      <span className="text-gray-700 font-medium sm:w-40 flex-shrink-0">{field.label}:</span>
                      <span className="sm:ml-2 text-gray-900 break-words">{field.value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </FormSection>
      )}


    </>
  );
};

export default ProductAttributesSection;

